import { store } from "../store";
import { handleUnauthorizedError } from "./apiMiddleware";

const API_URL = `${import.meta.env.VITE_API_URL || "http://localhost:5000/api"}/ai/project`;

const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type ProjectActionType =
  | "PROJECT_SNAPSHOT"
  | "DRAFT_REPLY"
  | "EMAIL_SUMMARY"
  | "STRATEGIC_ADVICE";

export interface ProjectContextPayload {
  projectKey: string;
  projectType: string;
  title?: string;
  description?: string;
  lastEmailSubject?: string;
  lastEmailPreview?: string;
  lastEmailAt?: string;
  lastEmailFrom?: string;
  participants: string[];
  currentTasks?: string[];
  attachments?: Array<{ name?: string; contentType?: string }>
  relatedEntities?: {
    retailerId?: string;
    supplierId?: string;
    purchaseOrderIds?: string[];
  };
  relatedResourceRoots?: string[];
  language?: "FR" | "EN";
}

export interface HotMemoryItemPayload {
  id: string;
  type: string;
  label?: string;
  data?: Record<string, any>;
  pinned?: boolean;
}

export interface ProjectActionTarget {
  threadId?: string;
  messageId?: string;
  attachmentId?: string;
  resourcePath?: string;
  docKey?: string;
  collectionName?: string;
}

export interface ProjectRefPayload {
  retailerId?: string;
  projectId?: string;
}

export interface StyleProfilePayload {
  tone?: string;
  closing?: string;
  length?: string;
  firmness?: string;
  samples?: string[];
}

export interface RecipientProfilePayload {
  relation?: string;
  sensitivity?: string;
  formality?: string;
}

export interface ProjectActionResponse {
  structuredOutput: Record<string, any>;
  used_sources: any[];
  source_summary: Record<string, number>;
  language: "FR" | "EN";
  warnings: string[];
}

export const runProjectAction = async (payload: {
  action: ProjectActionType;
  projectContext: ProjectContextPayload;
  projectRef?: ProjectRefPayload;
  target?: ProjectActionTarget;
  hotMemory: HotMemoryItemPayload[];
  styleProfile?: StyleProfilePayload;
  recipientProfile?: RecipientProfilePayload;
}): Promise<ProjectActionResponse> => {
  const response = await fetch(`${API_URL}/action`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  return response.json();
};
